import React, { useState } from 'react';
import { useAlert } from 'react-alert';
import { Auth } from 'aws-amplify';
import * as Yup from 'yup';
import { ErrorMessage, Field, Formik } from 'formik';
import Base, { ContentContainer } from '../../components/Base';
import { Button, ButtonActionBack, DivActionBack, Form, FormControl, H1, Hint, ImgIconBack, InputError } from '../../assets/styles/GlobalStyles';
import { passwordValidation, verifyError } from '../../utils/verifyErrors';
import { Input } from '../../components/Base/Elements';
import IconBack from "../../assets/icon/icon-back.svg";
import { BgSelectAppWebForm, LogoContainer } from "../../components/Base/styled";
import LogoAppWebSelectFarm from "../../assets/images/fundo-rosa-cima.png";
import { verifyEmailExist } from '../../services/services';
import { translate } from '../../i18n/index';

const SignUp = ({ history }) => {
    const alert = useAlert();
    const hasUsername = history.location.state ? history.location.state.username : null;
    const [passwordValidate, setPasswordValidate] = useState({
        min: false,
        number: false,
    });

    function handleClickSignIn() {
        history.push({
            pathname: '/login',
            search: history.location.search,
        });
    }

    function checkDigitIsNumber(value) {
        return /^\d*$/.test(value);

    }

    return (
        <>
            <DivActionBack>
                <ButtonActionBack onClick={() => handleClickSignIn()} type="button">
                    <ImgIconBack src={IconBack} className="icon-back"
                        alt="Button Back" />{translate('buttonActionBack')}
                </ButtonActionBack>
            </DivActionBack>
            <div>
                <LogoContainer
                    style={{ alignSelf: 'center' }}
                    rel="noopener noreferrer"
                    target="_self">
                    <BgSelectAppWebForm src={LogoAppWebSelectFarm} alt="Logo OnFarm" title="OnFarm" />
                </LogoContainer>
            </div>
            <Base width={430} marginTop="450" isAppWeb={true} backgroundForm={'#fff'}>
                <ContentContainer>
                    <Formik
                        enableReinitialize
                        initialValues={{
                            name: '',
                            username: hasUsername ? hasUsername : '',
                            email: '',
                            password: '',
                            confirmPassword: ''
                        }}
                        validationSchema={Yup.object().shape({
                            name: Yup.string()
                                .required(translate('validateRequired'))
                                .min(5, translate('validatePasswordMin')),
                            username: Yup.string()
                                .required(translate('validateRequired'))
                                .min(5, translate('validateInvalidUsername')),
                            email: Yup.string()
                                .email(translate('validateInvalidEmail'))
                                .required(translate('validateRequired')),
                            password: Yup.string()
                                .required(translate('validateRequired'))
                                .min(6, translate('validatePasswordMin'))
                                .max(6, translate('validatePasswordMax'))
                                .matches(/^\d+$/, translate('validatePasswordOnlyNumbers')),
                            confirmPassword: Yup.string().when("password", {
                                is: val => (!!(val && val.length > 0)),
                                then: Yup.string().oneOf(
                                    [Yup.ref("password")],
                                    translate('validatePasswordNotEquals')
                                )
                            })
                        })}
                        onSubmit={async (values, actions) => {
                            console.log(values);
                            if (values) {
                                try {
                                    const data = {
                                        username: values.username.toLowerCase(),
                                        password: values.password,
                                        attributes: {
                                            email: values.email,
                                            name: values.name,
                                        },
                                    };

                                    let emailAlreadyExists = false;
                                    try {
                                        const emailExist = await verifyEmailExist(values.email);
                                        if (emailExist.data) {
                                            emailAlreadyExists = true;
                                        }
                                    } catch (e) {
                                        const status = e.response?.status;
                                        const description = e.response?.data?.['hydra:description'];
                                        if (status === 404 && description === 'User not found') {
                                            emailAlreadyExists = false;
                                        } else {
                                            throw e;
                                        }
                                    }

                                    if (emailAlreadyExists) {
                                        alert.show('Email já existe na base de dados', {
                                            closeCopy: 'Fechar',
                                        });
                                        return;
                                    }

                                    await Auth.signUp(data);

                                    history.push({
                                        pathname: '/confirmar',
                                        search: history.location.search,
                                        state: {
                                            username: values.username.toLowerCase(),
                                            password: values.password
                                        },
                                    })
                                } catch (e) {
                                    alert.show(verifyError(e.code, e.message), {
                                        closeCopy: 'Fechar',
                                    });
                                }
                            }
                            actions.setSubmitting(false);
                        }}>
                        {({ isSubmitting, handleChange }) => (
                            <Form>
                                <H1
                                    style={{
                                        marginBottom: '20px',
                                        lineHeight: '25px',
                                        fontSize: '15px',
                                    }}
                                >
                                    {translate('registerTitle')}</H1>
                                <>
                                    <FormControl>
                                        <Field
                                            type="name"
                                            name="name"
                                            onChange={handleChange}
                                            placeholder={translate('inputName')}
                                            component={Input}
                                        />
                                        <ErrorMessage name="name" component={InputError} />
                                    </FormControl>
                                    <FormControl>
                                        <Field
                                            type="username"
                                            name="username"
                                            onChange={handleChange}
                                            placeholder={translate('inputLogin')}
                                            component={Input}
                                        />
                                        <ErrorMessage name="username" component={InputError} />
                                    </FormControl>
                                    <FormControl>
                                        <Field
                                            type="email"
                                            name="email"
                                            onChange={handleChange}
                                            placeholder={translate('inputEmail')}
                                            component={Input}
                                        />
                                        <ErrorMessage name="email" component={InputError} />
                                    </FormControl>
                                    <FormControl>
                                        <Field
                                            type="password"
                                            name="password"
                                            onInput={(value) => {
                                                if (checkDigitIsNumber(value.target.value)) {
                                                    passwordValidation(value.target.value, setPasswordValidate);
                                                } else {
                                                    value.currentTarget.value = value.target.value.substr(0, value.target.value.length - 1);
                                                }
                                            }}
                                            placeholder={translate('inputNewPassword')}
                                            component={Input}
                                        />
                                        <ErrorMessage name="password" component={InputError} />
                                        <Hint>
                                            <div>{translate('inputPasswordHintTitle')}</div>
                                            <ul>
                                                <li className={`${passwordValidate.min ? 'complete' : ''}`}>
                                                    {translate('inputPasswordHint1')}
                                                </li>
                                            </ul>
                                        </Hint>
                                    </FormControl>
                                    <FormControl>
                                        <Field
                                            type="password"
                                            name="confirmPassword"
                                            onChange={handleChange}
                                            onInput={(value) => {
                                                if (!checkDigitIsNumber(value.target.value)) {
                                                    value.currentTarget.value = value.target.value.substr(0, value.target.value.length - 1);
                                                }
                                            }}
                                            placeholder={translate('inputConfirmNewPassword')}
                                            component={Input}
                                        />
                                        <ErrorMessage name="confirmPassword" component={InputError} />
                                    </FormControl>
                                </>

                                <FormControl>
                                    <Button type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? translate('buttonRegistering') : translate('buttonRegister')}
                                    </Button>
                                </FormControl>
                            </Form>
                        )}
                    </Formik>
                </ContentContainer>
            </Base>
        </>
    );
};

export default SignUp;
